import { ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import {
  AppBar, Badge, Box, Button, Container, CssBaseline,
  IconButton, Toolbar, Typography
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { convertUserImage } from "../../util/utils";
import { useAppDispatch } from "../../redux/store";
import setAuthToken from "../../services/axios-util/set-auth-token";
import { reset } from "../redux/adminAuthReducer";
import { reset as resetError } from "../redux/errorReducer";
import loginServices from "../services/loginServices";
import { unauthrizedError } from "../util/util";
import ErrorMessageBanner from "./ErrorMessageBanner";

const defaultTheme = createTheme();

interface Props {
  children: ReactNode;
}

export default function AdminPageFrame({children}: Props) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { admin } = useSelector((state: any) => state.adminAuth);

  const { errorObj } = useSelector((state: any) => state.mngError);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!errorObj) return

    const e = errorObj
    dispatch(resetError())
    if (typeof e === "string") {
      setErrors([e])
    } else if (e.response) {
      const message = unauthrizedError(e.response.status, dispatch)
      const responseErrors = e.response.data?.message ?? [T("api_unknown_error")]
      setErrors(message ?? responseErrors)
    } else {
      setErrors([String(e)])
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorObj])

  const logout = async () => {
    try {
      await loginServices.logout();
    } catch (error: any) {
      // pass
    } finally {
      dispatch(reset());
      setAuthToken("__");
    }
  }

  const subTitle = (): ReactNode => {
    if (location.pathname.indexOf("adminhome") > -1) return undefined;

    const key = location.pathname.replace(/^\//, "").replaceAll("/", "_")
    return (<Typography color="inherit">{`> ${T(key)}`}</Typography>)
  }

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar position="absolute">
          <Toolbar
            sx={{
              pr: 2,
            }}
          >
            <Typography
              component="h1"
              variant="h6"
              color="inherit"
              noWrap
              sx={styles.title}
            >
              <Button variant="text" color="inherit" onClick={() => navigate("/adminhome")}>{T("mng_title")}</Button>
              {subTitle()}
            </Typography>
            {admin?.image ? (
              <img
                src={convertUserImage(admin.image)}
                alt={T("mng_user_icon")}
                style={styles.userImage}
              />
            ):(
              <IconButton color="inherit">
                <Badge color="secondary">
                  <AccountCircleIcon />
                </Badge>
              </IconButton>
            )}

            <Typography>{admin?.nickname}</Typography>
            <IconButton
              onClick={() => logout()}
              color="inherit"
            >
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === "light"
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            height: "100vh",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container style={{ marginTop: 4, marginBottom: 4 }}>
            {children}
          </Container>
        </Box>
      </Box>
      <ErrorMessageBanner
        open={errors.length !== 0}
        messages={errors}
        onClose={() => setErrors([])}
      />
    </ThemeProvider>
  );
}

const styles = {
  title: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    "& .MuiButtonBase-root": {
      fontSize: "inherit",
    },
    "& .MuiTypography-root": {
      fontSize: "inherit",
    }
  },
  userImage: {
    width: "1.25rem",
    height: "1.25rem",
    alignSelf: "center",
    marginRight: "10px",
    borderRadius: 35,
  },
}
